import { getV, setV } from "../../core/binding";
import { runRules } from "../../core/ruleEngine";
import { getS, setS } from "../../core/state";
import { formatSkuBadgeText, formatVendorBadgeLabel, resolveVendorKeyForBadge } from "../../core/vendorDisplay";
import { validate } from "../../core/validation";
import { getAuthUser } from "../../core/auth";

import TextBox from "../components/TextBox";
import Dropdown from "../components/Dropdown";
import TextArea from "../components/TextArea";
import Checkbox from "../components/Checkbox";
import Toggle from "../components/Toggle";
import FileUpload from "../components/FileUpload";
import UploadApiField from "../components/UploadApiField";
import ReviewFeedbackForm from "../components/ReviewFeedbackForm";
import TopNavMenu, { collectTopNavBinds } from "../components/TopNavMenu";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

const SHOP_FILTERS_LAYOUT = "lfx-shop-filters-layout";

/* =============================================
   FORM SECTION — JSON-driven form renderer
   Supports: textbox, dropdown, textarea, checkbox,
             toggle, file, radio
   ============================================= */
export default function FormSection({ section, actions }) {
  const cols = section.layout?.columns || 2;
  const screenState = getS();
  const errors = screenState.errors || {};
  const currentEditId = screenState.editId ?? null;
  const lastActionMarker = screenState.lastActionMarker ?? null;
  const navigate = useNavigate();
  const [formStatus, setFormStatus] = useState("");
  const [specOpen, setSpecOpen] = useState(false);
  const isCollapsibleFilters =
    section.type === "form" && section.collapsible === true && String(section.id || "") === "products";
  const initiallyHidden = isCollapsibleFilters && section.initiallyHidden === true;
  const [filtersVisible, setFiltersVisible] = useState(() => !initiallyHidden);
  const [filtersExpanded, setFiltersExpanded] = useState(() => section.defaultCollapsed === false);
  const [mediaJsonOpen, setMediaJsonOpen] = useState(false);

  useEffect(() => {
    // Clear stale messages when starting a new form session (Add Product / reset), not when
    // editId is first assigned after Save — that would wipe "Saved successfully" on first save.
    setFormStatus("");
  }, [lastActionMarker]);

  useEffect(() => {
    if (!isCollapsibleFilters) return;
    const onExpand = () => {
      setFiltersVisible(true);
      setFiltersExpanded(true);
      requestAnimationFrame(() => {
        document.getElementById("shop-filters-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" });
        document.getElementById("shop-filter-search")?.focus();
      });
    };
    window.addEventListener("lfx-expand-shop-filters", onExpand);
    return () => window.removeEventListener("lfx-expand-shop-filters", onExpand);
  }, [isCollapsibleFilters]);

  useLayoutEffect(() => {
    if (!isCollapsibleFilters) return;
    const compact = initiallyHidden && !filtersVisible;
    window.dispatchEvent(new CustomEvent(SHOP_FILTERS_LAYOUT, { detail: { compact } }));
  }, [isCollapsibleFilters, initiallyHidden, filtersVisible]);

  const handleChange = (control, value) => {
    if (
      (control?.type === "file" || control?.type === "uploadapi") &&
      value &&
      typeof value === "object" &&
      value.__structuredImport &&
      control.mapTo &&
      typeof control.mapTo === "object"
    ) {
      Object.entries(control.mapTo).forEach(([payloadKey, bindPath]) => {
        if (!bindPath) return;
        const next = value[payloadKey];
        if (next && typeof next === "object") {
          setV(bindPath, JSON.stringify(next, null, 2));
        } else {
          setV(bindPath, next ?? "");
        }
      });
      if (section.rules?.length) {
        runRules(section.rules, section.id);
      }
      return;
    }
    setV(control.bind, value);

    // Hook: onChange
    if (control.onChange) {
      const s = getS();
      console.log("onChange hook:", control.onChange, value);
    }

    // Fire rules on every change
    if (section.rules?.length) {
      runRules(section.rules, section.id);
    }
  };

  const handleTopNavReset = (control) => {
    collectTopNavBinds(control).forEach((bind) => setV(bind, ""));
    if (section.rules?.length) {
      runRules(section.rules, section.id);
    }
  };

  const handleAction = (action) => {
    switch (action.type) {
      case "save": {
        const ok = validate(section.controls || []);
        if (ok) {
          actions?.save?.();
          setFormStatus("Saved successfully.");
        } else {
          const errs = getS().errors || {};
          const detail = Object.entries(errs)
            .map(([id, msg]) => `${msg}`)
            .filter(Boolean)
            .join(" ");
          setFormStatus(
            detail ? `Cannot save. ${detail}` : "Cannot save. Please fix highlighted fields."
          );
        }
        break;
      }
      case "reset":
        setS({ form: {}, filters: {}, editId: null, errors: {}, lastActionMarker: Date.now() });
        setFormStatus("");
        if (section.rules?.length) runRules(section.rules, section.id);
        break;
      case "delete":
        actions?.delete?.();
        setFormStatus("Deleted successfully.");
        break;
      case "back":
        navigate(-1);
        break;
      case "login": {
        const ok = validate(section.controls || []);
        if (ok) actions?.login?.();
        break;
      }
      case "loginBiometric":
        actions?.loginBiometric?.();
        break;
      case "signup": {
        const ok = validate(section.controls || []);
        if (ok) actions?.signup?.();
        break;
      }
      case "saveProfile": {
        const ok = validate(section.controls || []);
        if (ok) actions?.saveProfile?.();
        break;
      }
      case "registerBiometric":
        actions?.registerBiometric?.();
        break;
      case "goto":
        if (action.to) navigate(action.to);
        break;
      case "addToCart":
        actions?.addToCart?.();
        break;
      case "placeOrder": {
        const ok = validate(section.controls || []);
        if (ok) actions?.placeOrder?.();
        break;
      }
      default:
        break;
    }
  };

  const getOfferTypeNorm = () => {
    const offerTypeControl = (section.controls || []).find((c) => c.id === "offerType");
    const raw = offerTypeControl?.bind ? getV(offerTypeControl.bind) : "";
    return String(raw || "").trim().toLowerCase();
  };
  const mediaJsonControlIds = new Set(["images", "availableColors", "colorImageMap", "variantGalleryMap", "productJson"]);
  const isAdminProductsForm = String(section.id || "").toLowerCase() === "adminproducts";
  const shouldRenderControl = (control) => {
    if (control.headerOnly) return false;
    if (isAdminProductsForm && mediaJsonControlIds.has(control.id) && !mediaJsonOpen) {
      return false;
    }
    // Keep admin offer section concise: show only controls relevant to selected offer type.
    if (isAdminProductsForm) {
      const offerType = getOfferTypeNorm();
      if (control.id === "offerPercent") return offerType === "percent";
      if (control.id === "flatDiscount") return offerType === "flat";
      if (control.id === "buyQty" || control.id === "getQty") return offerType === "buyxgety" || offerType === "buy x get y";
      // Badge copy is only used on cards/detail for flat + buyXgetY; percent uses computed "% OFF".
      if (control.id === "offerLabel") {
        return offerType === "flat" || offerType === "buyxgety" || offerType === "buy x get y";
      }
    }
    return true;
  };
  const visibleControls = (section.controls || []).filter(shouldRenderControl);
  const isProductInfo = section.id === "productInfo";
  const specControlIds = new Set(["specHeading", "fabric", "mainColor", "workType", "pattern"]);
  const topControls = isProductInfo ? visibleControls.filter((c) => !specControlIds.has(c.id)) : visibleControls;
  const specControls = isProductInfo ? visibleControls.filter((c) => specControlIds.has(c.id) && c.id !== "specHeading") : [];
  const specHeading = isProductInfo ? visibleControls.find((c) => c.id === "specHeading") : null;
  if (section.type === "form" && visibleControls.length === 0 && !(section.actions?.length)) {
    return null;
  }

  if (initiallyHidden && !filtersVisible) {
    return <div className="lfx-shop-filters-placeholder" aria-hidden />;
  }

  return (
    <div
      className={`lfx-card ${section.className || ""} ${isCollapsibleFilters ? "lfx-card--collapsible-filters" : ""}`}
      id={isCollapsibleFilters ? "shop-filters-anchor" : undefined}
    >
      {section.title && (
        <div className={`lfx-card-header ${isCollapsibleFilters ? "lfx-card-header--with-toggle" : ""}`}>
          <span className="lfx-card-title">{section.title}</span>
          {isCollapsibleFilters ? (
            <button
              type="button"
              className="lfx-btn secondary sm lfx-filter-toggle-btn"
              aria-expanded={filtersExpanded}
              onClick={() => setFiltersExpanded((v) => !v)}
            >
              {filtersExpanded ? "Hide filters" : "Show filters"}
            </button>
          ) : null}
        </div>
      )}

      <div
        className={`lfx-card-body ${isCollapsibleFilters && !filtersExpanded ? "lfx-card-body--collapsed" : ""}`}
      >
        <div className={`lfx-form-grid cols-${cols}`}>
          {topControls.map((ctrl) => (
            <span key={`ctrl-wrap-${ctrl.id}`} style={{ display: "contents" }}>
              <div
                className={`lfx-field ${ctrl.colSpan ? `span-${ctrl.colSpan}` : ""} ${ctrl.className || ""} ${ctrl.type === "sectionheader" ? "lfx-form-section-header-wrap" : ""}`}
              >
                {!ctrl.hideLabel && ctrl.type !== "sectionheader" ? <label className="lfx-label">{ctrl.label}</label> : null}
                <ControlRenderer
                  control={ctrl}
                  value={getV(ctrl.bind)}
                  onChange={(v) => handleChange(ctrl, v)}
                  onCompositeChange={(bind, v) => handleChange({ bind }, v)}
                  onResetSection={() => handleAction({ type: "reset" })}
                  onTopNavReset={() => handleTopNavReset(ctrl)}
                  widgetResetToken={`${String(currentEditId ?? "")}:${String(lastActionMarker ?? "")}`}
                />
                {errors[ctrl.id] ? <div className="lfx-error">{errors[ctrl.id]}</div> : null}
              </div>
              {isAdminProductsForm && ctrl.id === "multiImageImport" ? (
                <div className={`lfx-field ${cols > 1 ? "span-2" : "span-1"}`}>
                  <button
                    type="button"
                    className="lfx-btn secondary sm"
                    onClick={() => setMediaJsonOpen((v) => !v)}
                  >
                    {mediaJsonOpen ? "Hide generated media JSON fields" : "Show generated media JSON fields"}
                  </button>
                </div>
              ) : null}
            </span>
          ))}
          {isProductInfo && specControls.length > 0 ? (
            <div className="lfx-field span-1">
              <button
                type="button"
                className="lfx-btn secondary sm"
                onClick={() => setSpecOpen((v) => !v)}
              >
                {specOpen ? "Hide Specifications" : "Show Specifications"}
              </button>
              {specOpen ? (
                <div style={{ marginTop: 10, border: "1px solid #f0ebe4", borderRadius: 8, padding: 10 }}>
                  {specHeading ? (
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#5c4a38", marginBottom: 8 }}>
                      {getV(specHeading.bind) || specHeading.label}
                    </div>
                  ) : null}
                  {specControls.map((ctrl) => (
                    <div key={`spec-${ctrl.id}`} style={{ marginBottom: 8 }}>
                      {!ctrl.hideLabel ? <label className="lfx-label">{ctrl.label}</label> : null}
                      <ControlRenderer
                        control={ctrl}
                        value={getV(ctrl.bind)}
                        onChange={(v) => handleChange(ctrl, v)}
                        onCompositeChange={(bind, v) => handleChange({ bind }, v)}
                        onResetSection={() => handleAction({ type: "reset" })}
                        onTopNavReset={() => handleTopNavReset(ctrl)}
                        widgetResetToken={`${String(currentEditId ?? "")}:${String(lastActionMarker ?? "")}`}
                      />
                      {errors[ctrl.id] ? <div className="lfx-error">{errors[ctrl.id]}</div> : null}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        {section.actions?.length > 0 && (
          <div className="lfx-form-actions">
            {section.actions.map((action, i) => (
              <button
                key={i}
                className={`lfx-btn ${action.variant || "secondary"}`}
                onClick={() => handleAction(action)}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
        {formStatus ? (
          <div
            style={{
              marginTop: 10,
              fontSize: 12,
              color: formStatus.startsWith("Saved") ? "#2d7a3a" : "#c0392b",
            }}
          >
            {formStatus}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* ===== CONTROL RENDERER ===== */
function ControlRenderer({ control, value, onChange, onCompositeChange, onResetSection, onTopNavReset, widgetResetToken }) {
  const props = { value, onChange, ...control };

  switch (control.type) {
    case "textbox":
      return <TextBox {...props} />;
    case "dropdown":
      return <Dropdown {...props} />;
    case "textarea":
      return <TextArea {...props} />;
    case "checkbox":
      return <Checkbox {...props} />;
    case "toggle":
      return <Toggle {...props} />;
    case "file":
      return <FileUpload {...props} />;
    case "uploadapi":
      {
        const authUser = getAuthUser();
        const baseContext =
          control.contextBinds && typeof control.contextBinds === "object"
            ? Object.fromEntries(
                Object.entries(control.contextBinds).map(([k, bind]) => [k, bind ? getV(bind) : ""])
              )
            : {};
        const uploadContext = {
          ...baseContext,
          vendorId: baseContext.vendorId || authUser?.email || authUser?.name || "",
          email: baseContext.email || authUser?.email || "",
        };
      return (
        <UploadApiField
          {...props}
          uploadContext={uploadContext}
          widgetResetToken={widgetResetToken}
        />
      );
      }
    case "topnavmenu":
      return (
        <TopNavMenu
          variant={control.variant || "simple"}
          items={control.items || []}
          groups={control.groups || []}
          theme={control.theme || ""}
          openOn={control.openOn ?? "hover"}
          getValue={(bind) => getV(bind)}
          onItemChange={(item, v) => item?.bind && onCompositeChange?.(item.bind, v)}
          onReset={() => onTopNavReset?.()}
          onResetAll={() => onResetSection?.()}
          showReset={control.showReset !== false}
          resetLabel={control.resetLabel || "Reset"}
          resetScope={control.resetScope || "nav"}
        />
      );
    case "label": {
      const emptyText = control.emptyText !== undefined ? control.emptyText : "—";
      if (control.format === "vendorCode") {
        const galleryRaw = control.vendorGalleryFallbackBind ? getV(control.vendorGalleryFallbackBind) : null;
        const key = resolveVendorKeyForBadge(value, normalizeImageArray(galleryRaw));
        const code = formatVendorBadgeLabel(key);
        if (!code) return null;
        return (
          <div className={`lfx-input ${control.valueClassName || ""}`} style={{ minHeight: "auto" }}>
            <span className="pd-vendor-code-value">{code}</span>
          </div>
        );
      }
      if (control.format === "skuCode") {
        const idFb = control.skuIdFallbackBind ? getV(control.skuIdFallbackBind) : "";
        const text = formatSkuBadgeText(value, idFb);
        if (!text) return null;
        return (
          <div className={`lfx-input ${control.valueClassName || ""}`} style={{ minHeight: "auto" }}>
            <span className="pd-sku-code-value">{text}</span>
          </div>
        );
      }
      if (control.hideWhenEmpty) {
        const isEmpty = value === undefined || value === null || String(value).trim() === "";
        if (isEmpty) return null;
      }
      if (control.format === "inr") {
        return (
          <div className={`lfx-input ${control.valueClassName || ""}`} style={{ minHeight: 38 }}>
            {value !== undefined && value !== null && value !== "" ? `Rs. ${Number(value).toLocaleString("en-IN")}` : emptyText}
          </div>
        );
      }
      return <div className={`lfx-input ${control.valueClassName || ""}`} style={{ minHeight: 38 }}>{value || emptyText}</div>;
    }
    case "image":
      return (
        <ImagePreview
          value={value}
          colorMapValue={control.colorMapBind ? getV(control.colorMapBind) : undefined}
          colorListValue={control.colorsBind ? getV(control.colorsBind) : undefined}
          variantMapValue={control.variantMapBind ? getV(control.variantMapBind) : undefined}
          offerPrice={control.offerPriceBind ? getV(control.offerPriceBind) : undefined}
          mrp={control.mrpBind ? getV(control.mrpBind) : undefined}
          offerType={control.offerTypeBind ? getV(control.offerTypeBind) : undefined}
          offerActive={control.offerActiveBind ? getV(control.offerActiveBind) : undefined}
          offerPercent={control.offerPercentBind ? getV(control.offerPercentBind) : undefined}
          flatDiscount={control.flatDiscountBind ? getV(control.flatDiscountBind) : undefined}
          buyQty={control.buyQtyBind ? getV(control.buyQtyBind) : undefined}
          getQty={control.getQtyBind ? getV(control.getQtyBind) : undefined}
          offerLabel={control.offerLabelBind ? getV(control.offerLabelBind) : undefined}
          latestFlag={control.latestFlagBind ? getV(control.latestFlagBind) : undefined}
          uploadedAt={control.uploadedAtBind ? getV(control.uploadedAtBind) : undefined}
          updatedAt={control.updatedAtBind ? getV(control.updatedAtBind) : undefined}
          stockValue={control.stockBind ? getV(control.stockBind) : undefined}
          outOfStock={control.outOfStockBind ? getV(control.outOfStockBind) : undefined}
          stockCount={control.stockCountBind ? getV(control.stockCountBind) : undefined}
          stockStatus={control.stockStatusBind ? getV(control.stockStatusBind) : undefined}
          selectedColorBind={control.selectedColorBind}
          lightbox={control.lightbox !== false}
        />
      );
    case "sectionheader":
      return (
        <div className="lfx-form-section-header">
          <div className="lfx-form-section-title">{control.title || control.label || ""}</div>
          {control.hint ? <div className="lfx-form-section-hint">{control.hint}</div> : null}
        </div>
      );
    case "reviewfeedback":
      return (
        <ReviewFeedbackForm
          value={value}
          onChange={onChange}
          productName={control.productNameBind ? getV(control.productNameBind) : ""}
          maxChars={control.maxChars || 2000}
          maxPhotos={control.maxPhotos || 5}
          showButtonLabel={control.showButtonLabel || "Write a review"}
          submitLabel={control.submitLabel || "Submit review"}
        />
      );
    default:
      return <TextBox {...props} />;
  }
}

const parseColorImageMap = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) return value;
  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch (e) {
      return {};
    }
  }
  return {};
};

const normalizeImageArray = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((v) => (typeof v === "string" ? v.trim() : ""))
      .filter(Boolean);
  }
  if (typeof value === "string" && value.trim()) {
    const raw = value.trim();
    if (raw.startsWith("data:image/")) return [raw];
    if (raw.startsWith("[") && raw.endsWith("]")) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          return parsed
            .map((v) => (typeof v === "string" ? v.trim() : ""))
            .filter(Boolean);
        }
      } catch (e) {
        // fall through to comma-based parse
      }
    }
    if (raw.includes(",") && !raw.startsWith("data:")) {
      return raw.split(",").map((v) => v.trim()).filter(Boolean);
    }
    return [raw];
  }
  return [];
};

const parseVariantGalleryMap = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) return value;
  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch (e) {
      return {};
    }
  }
  return {};
};

const ANGLE_ORDER = ["front", "draped", "pallu", "border", "zoom", "back"];
const toAngleLabel = (angle, index) => {
  if (!angle) return `Index ${index + 1}`;
  const normalized = String(angle).trim().toLowerCase();
  const hit = ANGLE_ORDER.find((a) => a === normalized);
  if (hit) return hit.charAt(0).toUpperCase() + hit.slice(1);
  return `Index ${index + 1}`;
};
const parseUploadedMeta = (url) => {
  const s = String(url || "");
  const parts = s.split("/").filter(Boolean);
  const upIdx = parts.indexOf("uploaded-products");
  if (upIdx < 0) return null;
  const segs = parts.slice(upIdx + 1);
  if (segs.length >= 4 && String(segs[2] || "").toLowerCase() === "unstructured") {
    return {
      vendorId: segs[0],
      sku: segs[1],
      color: "gallery",
      angle: "",
      unstructured: true,
    };
  }
  if (segs.length >= 5) {
    return {
      vendorId: segs[0],
      sku: segs[1],
      color: String(segs[2] || "").toLowerCase(),
      angle: String(segs[3] || "").toLowerCase(),
    };
  }
  if (segs.length >= 4) {
    return {
      sku: segs[0],
      color: String(segs[1] || "").toLowerCase(),
      angle: String(segs[2] || "").toLowerCase(),
    };
  }
  return null;
};

function ImagePreview({
  value,
  colorMapValue,
  colorListValue,
  variantMapValue,
  offerPrice,
  mrp,
  offerType,
  offerActive,
  offerPercent,
  flatDiscount,
  buyQty,
  getQty,
  offerLabel,
  latestFlag,
  uploadedAt,
  updatedAt,
  stockValue,
  outOfStock,
  stockCount,
  stockStatus,
  selectedColorBind,
  lightbox = true,
}) {
  const toTitle = (s) => {
    const raw = String(s || "").trim();
    if (!raw) return "";
    return raw
      .split(/\s+/)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(" ");
  };
  const pickColorKey = (map, color) => {
    if (!map || typeof map !== "object") return "";
    const keys = Object.keys(map);
    const target = String(color || "").trim().toLowerCase();
    if (!target) return "";
    return keys.find((k) => String(k || "").trim().toLowerCase() === target) || "";
  };

  const images = useMemo(() => normalizeImageArray(value), [value]);
  const colorImageMap = useMemo(() => parseColorImageMap(colorMapValue), [colorMapValue]);
  const variantGalleryMap = useMemo(() => parseVariantGalleryMap(variantMapValue), [variantMapValue]);
  const colorMetaOptions = useMemo(() => {
    const set = new Set();
    images.forEach((src) => {
      const color = parseUploadedMeta(src)?.color;
      if (color) set.add(toTitle(color));
    });
    return Array.from(set);
  }, [images]);
  const colorOptions = useMemo(() => {
    if (Array.isArray(colorListValue) && colorListValue.length) {
      const fromList = colorListValue.map((v) => String(v).trim()).filter(Boolean);
      const fromMeta = colorMetaOptions.filter(
        (color) => !fromList.some((x) => x.toLowerCase() === color.toLowerCase())
      );
      return [...fromList, ...fromMeta];
    }
    if (typeof colorListValue === "string" && colorListValue.trim()) {
      const fromList = colorListValue.split(",").map((v) => v.trim()).filter(Boolean);
      const fromMeta = colorMetaOptions.filter(
        (color) => !fromList.some((x) => x.toLowerCase() === color.toLowerCase())
      );
      return [...fromList, ...fromMeta];
    }
    const merged = [...Object.keys(variantGalleryMap), ...Object.keys(colorImageMap), ...colorMetaOptions];
    const out = [];
    merged.forEach((color) => {
      const normalized = String(color || "").trim();
      if (!normalized) return;
      if (!out.some((x) => x.toLowerCase() === normalized.toLowerCase())) out.push(normalized);
    });
    return out;
  }, [colorListValue, colorImageMap, variantGalleryMap, colorMetaOptions]);

  const [index, setIndex] = useState(0);
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedAngle, setSelectedAngle] = useState("");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const topThumbsRef = useRef(null);
  const colorThumbsRef = useRef(null);
  const touchStartXRef = useRef(null);
  const [topStripNav, setTopStripNav] = useState({ show: false, canPrev: false, canNext: false });
  const [colorStripNav, setColorStripNav] = useState({ show: false, canPrev: false, canNext: false });

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightboxOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightboxOpen]);
  useEffect(() => {
    if (!colorOptions.length) {
      setSelectedColor("");
      return;
    }
    if (!selectedColor || !colorOptions.includes(selectedColor)) {
      setSelectedColor(colorOptions[0]);
    }
  }, [colorOptions, selectedColor]);

  useEffect(() => {
    if (!selectedColorBind) return;
    setV(selectedColorBind, selectedColor || "");
  }, [selectedColor, selectedColorBind]);

  const colorScopedImages = useMemo(() => {
    if (!selectedColor) return images;
    const colorKey = selectedColor.toLowerCase();
    const byColor = images.filter((u) => parseUploadedMeta(u)?.color === colorKey);
    const variantKey = pickColorKey(variantGalleryMap, selectedColor);
    const variantShots = variantKey && variantGalleryMap?.[variantKey] && typeof variantGalleryMap[variantKey] === "object"
      ? Object.values(variantGalleryMap[variantKey]).map((u) => String(u || "").trim()).filter(Boolean)
      : [];
    const colorMapKey = pickColorKey(colorImageMap, selectedColor);
    const mapShot = colorMapKey ? [String(colorImageMap[colorMapKey] || "").trim()].filter(Boolean) : [];
    const scoped = [...new Set([...byColor, ...variantShots, ...mapShot])];
    return scoped.length ? scoped : images;
  }, [images, selectedColor, variantGalleryMap, colorImageMap]);

  const angleBuckets = useMemo(() => {
    const buckets = {};
    colorScopedImages.forEach((src) => {
      const angle = parseUploadedMeta(src)?.angle || "";
      const key = angle || "index";
      if (!buckets[key]) buckets[key] = [];
      buckets[key].push(src);
    });
    return buckets;
  }, [colorScopedImages]);

  const angleEntries = useMemo(() => {
    if (!selectedColor) return [];
    const variantKey = pickColorKey(variantGalleryMap, selectedColor);
    const shots = variantKey ? variantGalleryMap?.[variantKey] : null;
    if (!shots || typeof shots !== "object" || !Object.keys(shots).length) return [];
    const urls = [];
    ANGLE_ORDER.forEach((k) => {
      const u = typeof shots[k] === "string" ? shots[k].trim() : "";
      if (u) urls.push({ angle: k, src: u });
    });
    Object.entries(shots).forEach(([k, v]) => {
      const exists = urls.some((x) => x.angle === k);
      if (!exists && typeof v === "string" && v.trim()) urls.push({ angle: k, src: v.trim() });
    });
    return urls;
  }, [selectedColor, variantGalleryMap]);

  const angleKeys = useMemo(() => {
    const keys = Object.keys(angleBuckets);
    const ordered = ANGLE_ORDER.filter((k) => keys.includes(k));
    const others = keys.filter((k) => !ordered.includes(k));
    return [...ordered, ...others];
  }, [angleBuckets]);
  const hasAngleNav = angleKeys.length > 1;
  const leftAngleEntries = useMemo(() => {
    if (!hasAngleNav) return [];
    return angleKeys.map((angle, i) => ({
      angle,
      label: toAngleLabel(angle, i),
      thumb: angleBuckets[angle]?.[0] || "",
    }));
  }, [hasAngleNav, angleKeys, angleBuckets]);
  const leftImageEntries = useMemo(() => {
    if (hasAngleNav || colorScopedImages.length <= 1) return [];
    return colorScopedImages.map((src, i) => ({
      key: `image-${i}`,
      label: `Image ${i + 1}`,
      thumb: src,
      idx: i,
    }));
  }, [hasAngleNav, colorScopedImages]);

  const displayImages = useMemo(() => {
    if (hasAngleNav) return angleBuckets[selectedAngle] || [];
    return colorScopedImages;
  }, [hasAngleNav, angleBuckets, selectedAngle, colorScopedImages]);
  const imageLabels = displayImages.map((_, i) => `Index ${i + 1}`);

  useEffect(() => {
    if (displayImages.length === 0) {
      setIndex(0);
      return;
    }
    if (index > displayImages.length - 1) setIndex(0);
  }, [displayImages, index]);
  useEffect(() => {
    if (!hasAngleNav) {
      setSelectedAngle("");
      setIndex(0);
      return;
    }
    const next = angleKeys.includes("front") ? "front" : angleKeys[0] || "";
    setSelectedAngle(next);
    setIndex(0);
  }, [selectedColor, hasAngleNav, angleKeys]);

  const mappedColorKey = selectedColor ? pickColorKey(colorImageMap, selectedColor) : "";
  const mappedColorImage = mappedColorKey ? colorImageMap?.[mappedColorKey] : "";
  const current = displayImages[index] || mappedColorImage || "";
  const toBool = (v) => {
    if (v === true || v === 1) return true;
    const s = String(v ?? "").trim().toLowerCase();
    return s === "true" || s === "1" || s === "yes" || s === "on";
  };
  const isWithinWeek = (dt) => {
    if (!dt) return false;
    const d = new Date(dt);
    if (Number.isNaN(d.getTime())) return false;
    const diff = Date.now() - d.getTime();
    return diff >= 0 && diff <= 7 * 24 * 60 * 60 * 1000;
  };
  const latestByDate = isWithinWeek(uploadedAt || updatedAt);
  const showLatest = toBool(latestFlag) || latestByDate;
  const toAmount = (v) => {
    const n = Number(String(v ?? "").replace(/[^0-9.-]/g, ""));
    return Number.isFinite(n) ? n : 0;
  };
  const mrpNum = toAmount(mrp);
  const offerNum = toAmount(offerPrice);
  const offerTypeNorm = String(offerType || "").trim().toLowerCase();
  const offerOn = toBool(offerActive) || (offerActive === undefined || offerActive === null || offerActive === "");
  const offerPercentNum = Number(offerPercent);
  const flatDiscountNum = Number(flatDiscount);
  const buyNum = Number(buyQty);
  const getNum = Number(getQty);
  const isBuyXGetY = (offerTypeNorm === "buyxgety" || offerTypeNorm === "buy x get y") && offerOn;
  const isFlatOffer = offerTypeNorm === "flat" && offerOn;
  const isPercentOffer = offerTypeNorm === "percent" && offerOn;
  const buyXGetYLabel = String(offerLabel || "").trim() || `BUY ${Number.isFinite(buyNum) && buyNum > 0 ? buyNum : 2} GET ${Number.isFinite(getNum) && getNum > 0 ? getNum : 1}`;
  const flatOfferLabel = String(offerLabel || "").trim() || `FLAT ₹${Number.isFinite(flatDiscountNum) && flatDiscountNum > 0 ? flatDiscountNum : 500} OFF`;
  const discountPct = mrpNum > 0 && offerNum > 0 && mrpNum > offerNum
    ? Math.round(((mrpNum - offerNum) / mrpNum) * 100)
    : 0;
  const effectivePercent = discountPct > 0
    ? discountPct
    : (isPercentOffer && Number.isFinite(offerPercentNum) && offerPercentNum > 0 ? Math.round(offerPercentNum) : 0);
  const stockText = String(stockValue || "").toLowerCase();
  const statusText = String(stockStatus || "").toLowerCase();
  const stockNum = Number(stockCount);
  const isOut = toBool(outOfStock) || statusText === "out of stock" || stockText.includes("out of stock");
  const isLimited = !isOut && ((Number.isFinite(stockNum) && stockNum > 0 && stockNum < 5) || stockText.includes("limited"));
  const stockBadge = isOut ? "Out of Stock" : isLimited ? "Limited Stock" : "";
  const stockBadgeClass = isOut ? "out" : isLimited ? "limited" : "";

  const fmtInr = (n) =>
    Number.isFinite(n) && n >= 0 ? `₹${Number(n).toLocaleString("en-IN")}.00` : "";
  let lightboxOfferCaption = "";
  if (isBuyXGetY) lightboxOfferCaption = buyXGetYLabel;
  else if (isFlatOffer) lightboxOfferCaption = flatOfferLabel;
  else if (isPercentOffer) {
    const lbl = String(offerLabel || "").trim();
    lightboxOfferCaption = lbl || (effectivePercent > 0 ? `${effectivePercent}% OFF` : "");
  } else if (mrpNum > 0 && offerNum > 0 && mrpNum > offerNum && discountPct > 0) {
    lightboxOfferCaption = `${discountPct}% OFF`;
  }
  const showLightboxPricing = mrpNum > 0 || offerNum > 0 || Boolean(String(lightboxOfferCaption || "").trim());

  const hasThumbColumn = leftAngleEntries.length > 0 || leftImageEntries.length > 0;
  const leftEntries = leftAngleEntries.length > 0 ? leftAngleEntries : leftImageEntries;
  const isLeftAngleMode = leftAngleEntries.length > 0;
  const hasMainNav = displayImages.length > 1 || (isLeftAngleMode && leftEntries.length > 1);
  const itemName = String(getS()?.form?.name || "").trim();
  const vendorKey = resolveVendorKeyForBadge(String(getS()?.form?.vendorId || "").trim(), [
    current,
    displayImages[index],
    displayImages[0],
    colorScopedImages[0],
    ...normalizeImageArray(value),
  ]);
  const vendorBadge = formatVendorBadgeLabel(vendorKey);
  const skuDisplay = formatSkuBadgeText(getS()?.form?.sku, getS()?.form?.id);
  const currentAngleLabel = isLeftAngleMode
    ? toAngleLabel(selectedAngle, 0)
    : (imageLabels[index] || "");
  const computeStripNav = (ref, setter, opts = {}) => {
    const minItemCount = Number(opts.minItemCount) || 0;
    const el = ref?.current;
    if (!el) {
      setter({ show: false, canPrev: false, canNext: false });
      return;
    }
    if (minItemCount < 2) {
      setter({ show: false, canPrev: false, canNext: false });
      return;
    }
    const clientW = el.clientWidth || 0;
    const scrollW = el.scrollWidth || 0;
    const max = Math.max(0, scrollW - clientW);
    const estRowWidth = 62 * minItemCount + 8 * Math.max(0, minItemCount - 1);
    const estOverflow = clientW > 0 && estRowWidth > clientW + 6;
    const measuredOverflow = max > 0.5;
    const show = measuredOverflow || estOverflow;
    const left = el.scrollLeft;
    const canPrev = show && left > 0.5;
    const canNext = show && left + clientW < scrollW - 0.5;
    setter({ show, canPrev, canNext });
  };
  const scrollStrip = (ref, delta, setter, stripOpts = {}) => {
    if (!ref?.current) return;
    ref.current.scrollBy({ left: delta, behavior: "smooth" });
    window.setTimeout(() => computeStripNav(ref, setter, stripOpts), 220);
    window.setTimeout(() => computeStripNav(ref, setter, stripOpts), 520);
  };
  useLayoutEffect(() => {
    const topOpts = { minItemCount: leftEntries.length };
    const colorOpts = { minItemCount: colorOptions.length };
    const refresh = () => {
      computeStripNav(topThumbsRef, setTopStripNav, topOpts);
      computeStripNav(colorThumbsRef, setColorStripNav, colorOpts);
    };
    refresh();
    const t0 = window.setTimeout(refresh, 0);
    const t1 = window.setTimeout(refresh, 320);
    const raf = window.requestAnimationFrame(refresh);
    const nodes = [topThumbsRef.current, colorThumbsRef.current].filter(Boolean);
    let ro;
    if (typeof ResizeObserver !== "undefined" && nodes.length) {
      ro = new ResizeObserver(() => refresh());
      nodes.forEach((n) => ro.observe(n));
    }
    window.addEventListener("resize", refresh);
    return () => {
      window.clearTimeout(t0);
      window.clearTimeout(t1);
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", refresh);
      ro?.disconnect();
    };
  }, [leftEntries.length, colorOptions.length, selectedColor, hasThumbColumn]);
  useLayoutEffect(() => {
    const el = topThumbsRef.current;
    if (!el) return;
    const active = el.querySelector(".lfx-pd-gallery-thumb-btn--active");
    if (active && typeof active.scrollIntoView === "function") {
      active.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" });
    }
  }, [index, selectedAngle, leftEntries.length]);
  const goPrevMain = () => {
    if (displayImages.length > 1) {
      setIndex((i) => (i - 1 + displayImages.length) % displayImages.length);
      return;
    }
    if (isLeftAngleMode && leftEntries.length > 1) {
      const cur = leftEntries.findIndex((x) => x.angle === selectedAngle);
      const next = (cur - 1 + leftEntries.length) % leftEntries.length;
      setSelectedAngle(leftEntries[next]?.angle || selectedAngle);
      setIndex(0);
    }
  };
  const goNextMain = () => {
    if (displayImages.length > 1) {
      setIndex((i) => (i + 1) % displayImages.length);
      return;
    }
    if (isLeftAngleMode && leftEntries.length > 1) {
      const cur = leftEntries.findIndex((x) => x.angle === selectedAngle);
      const next = (cur + 1) % leftEntries.length;
      setSelectedAngle(leftEntries[next]?.angle || selectedAngle);
      setIndex(0);
    }
  };
  const onMainTouchStart = (e) => {
    touchStartXRef.current = e.touches?.[0]?.clientX ?? null;
  };
  const onMainTouchEnd = (e) => {
    const start = touchStartXRef.current;
    const end = e.changedTouches?.[0]?.clientX ?? null;
    touchStartXRef.current = null;
    if (start == null || end == null) return;
    const delta = end - start;
    if (Math.abs(delta) < 30) return;
    if (delta > 0) goPrevMain();
    else goNextMain();
  };

  if (!current) return <div className="lfx-input">No image</div>;

  return (
    <div className="lfx-pd-gallery">
      <div className="lfx-pd-gallery-panel">
        {hasThumbColumn ? (
          <div className="lfx-pd-gallery-strip">
            {topStripNav.show ? (
              <button
                type="button"
                className={`lfx-pd-strip-nav${topStripNav.canPrev ? "" : " is-disabled"}`}
                onClick={() => scrollStrip(topThumbsRef, -220, setTopStripNav, { minItemCount: leftEntries.length })}
                aria-label="Scroll thumbnails left"
              >
                <ChevronLeft size={14} />
              </button>
            ) : null}
            <div
              className={`lfx-pd-gallery-thumbs${topStripNav.show ? " has-strip-nav" : ""}`}
              role="list"
              aria-label="Product views"
              ref={topThumbsRef}
              onScroll={() => computeStripNav(topThumbsRef, setTopStripNav, { minItemCount: leftEntries.length })}
            >
              {leftEntries.map((entry, i) => {
                const active = isLeftAngleMode
                  ? selectedAngle === entry.angle
                  : index === (entry.idx ?? -1);
                return (
                  <div
                    key={entry.key || `angle-${entry.angle}-${i}`}
                    className="lfx-pd-gallery-thumb-wrap"
                    role="listitem"
                  >
                    <button
                      type="button"
                      className={`lfx-pd-gallery-thumb-btn${active ? " lfx-pd-gallery-thumb-btn--active" : ""}`}
                      onClick={() => {
                        if (isLeftAngleMode) {
                          setSelectedAngle(entry.angle);
                          setIndex(0);
                        } else {
                          setIndex(entry.idx ?? 0);
                        }
                      }}
                      title={`View ${entry.label || `Image ${i + 1}`}`}
                    >
                      <img
                        src={entry.thumb}
                        alt=""
                        loading="lazy"
                        className="lfx-pd-gallery-thumb-img"
                        onLoad={() => computeStripNav(topThumbsRef, setTopStripNav, { minItemCount: leftEntries.length })}
                      />
                      {stockBadge ? <span className={`lfx-stock-badge lfx-stock-badge--thumb ${stockBadgeClass}`}>{stockBadge}</span> : null}
                    </button>
                    <div className="lfx-pd-gallery-thumb-caption">
                      {entry.label || `Image ${i + 1}`}
                    </div>
                  </div>
                );
              })}
            </div>
            {topStripNav.show ? (
              <button
                type="button"
                className={`lfx-pd-strip-nav${topStripNav.canNext ? "" : " is-disabled"}`}
                onClick={() => scrollStrip(topThumbsRef, 220, setTopStripNav, { minItemCount: leftEntries.length })}
                aria-label="Scroll thumbnails right"
              >
                <ChevronRight size={14} />
              </button>
            ) : null}
          </div>
        ) : null}
        <div className="lfx-pd-gallery-main">
          <div className="lfx-pd-gallery-main-frame">
            {lightbox ? (
              <button
                type="button"
                className="lfx-pd-gallery-main-hit"
                onClick={() => setLightboxOpen(true)}
                onTouchStart={onMainTouchStart}
                onTouchEnd={onMainTouchEnd}
                aria-label="View full size image"
              >
                <img
                  src={current}
                  alt=""
                  loading="lazy"
                  className="lfx-pd-gallery-main-img"
                />
              </button>
            ) : (
              <img
                src={current}
                alt=""
                loading="lazy"
                className="lfx-pd-gallery-main-img"
                onTouchStart={onMainTouchStart}
                onTouchEnd={onMainTouchEnd}
              />
            )}
            {(vendorBadge || skuDisplay) ? (
              <div className="lfx-product-thumb-codes lfx-product-thumb-codes--pd-main">
                {vendorBadge ? (
                  <span className="lfx-vendor-thumb-badge lfx-vendor-thumb-badge--detail">{vendorBadge}</span>
                ) : null}
                {skuDisplay ? (
                  <span className="lfx-sku-thumb-badge lfx-sku-thumb-badge--detail">{skuDisplay}</span>
                ) : null}
              </div>
            ) : null}
          </div>
          {isBuyXGetY ? (
            <span className="lfx-offer-badge lfx-offer-badge--detail">{buyXGetYLabel}</span>
          ) : isFlatOffer ? (
            <span className="lfx-offer-badge lfx-offer-badge--detail">{flatOfferLabel}</span>
          ) : effectivePercent > 0 ? (
            <span className="lfx-discount-badge lfx-discount-badge--detail">{effectivePercent}% OFF</span>
          ) : null}
          {showLatest ? <span className="lfx-latest-badge lfx-latest-badge--detail">Latest</span> : null}
          {stockBadge ? <span className={`lfx-stock-badge lfx-stock-badge--detail ${stockBadgeClass}`}>{stockBadge}</span> : null}
          {hasMainNav && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  goPrevMain();
                }}
                className="lfx-btn secondary sm lfx-pd-gallery-nav lfx-pd-gallery-nav--prev"
                aria-label="Previous image"
              >
                <ChevronLeft size={14} />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  goNextMain();
                }}
                className="lfx-btn secondary sm lfx-pd-gallery-nav lfx-pd-gallery-nav--next"
                aria-label="Next image"
              >
                <ChevronRight size={14} />
              </button>
            </>
          )}
        </div>
      </div>
      {lightboxOpen && lightbox && typeof document !== "undefined"
        ? createPortal(
            <div
              className="lfx-image-lightbox"
              role="dialog"
              aria-modal="true"
              aria-label="Full size image"
              onClick={() => setLightboxOpen(false)}
            >
              <button
                type="button"
                className="lfx-image-lightbox-close"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxOpen(false);
                }}
                aria-label="Close"
              >
                <X size={22} />
              </button>
              {hasMainNav ? (
                <>
                  <button
                    type="button"
                    className="lfx-image-lightbox-nav lfx-image-lightbox-nav--prev"
                    onClick={(e) => {
                      e.stopPropagation();
                      goPrevMain();
                    }}
                    aria-label="Previous image"
                  >
                    <ChevronLeft size={22} />
                  </button>
                  <button
                    type="button"
                    className="lfx-image-lightbox-nav lfx-image-lightbox-nav--next"
                    onClick={(e) => {
                      e.stopPropagation();
                      goNextMain();
                    }}
                    aria-label="Next image"
                  >
                    <ChevronRight size={22} />
                  </button>
                </>
              ) : null}
              {(itemName || selectedColor || currentAngleLabel) ? (
                <div className="lfx-image-lightbox-meta">
                  {itemName ? <div className="lfx-image-lightbox-meta-title">{itemName}</div> : null}
                  <div className="lfx-image-lightbox-meta-row">
                    {selectedColor ? <span className="lfx-image-lightbox-meta-chip">Color: {selectedColor}</span> : null}
                    {currentAngleLabel ? <span className="lfx-image-lightbox-meta-chip">View: {currentAngleLabel}</span> : null}
                  </div>
                </div>
              ) : null}
              <div className="lfx-image-lightbox-inner" onClick={(e) => e.stopPropagation()}>
                <span className="lfx-image-lightbox-img-wrap">
                  <img src={current} alt="" className="lfx-image-lightbox-img" />
                </span>
              </div>
              {(showLightboxPricing || vendorBadge || skuDisplay) ? (
                <div className="lfx-image-lightbox-price" onClick={(e) => e.stopPropagation()}>
                  {showLightboxPricing ? (
                    <>
                      {offerNum > 0 ? (
                        <>
                          <div className="lfx-image-lightbox-price-line lfx-image-lightbox-price-line--sale">
                            {fmtInr(offerNum)}
                          </div>
                          {mrpNum > offerNum ? (
                            <div className="lfx-image-lightbox-price-line lfx-image-lightbox-price-line--mrp">
                              MRP <s>{fmtInr(mrpNum)}</s>
                            </div>
                          ) : null}
                        </>
                      ) : mrpNum > 0 ? (
                        <div className="lfx-image-lightbox-price-line lfx-image-lightbox-price-line--sale">
                          {fmtInr(mrpNum)}
                          <span className="lfx-image-lightbox-price-muted"> MRP</span>
                        </div>
                      ) : null}
                      {String(lightboxOfferCaption || "").trim() ? (
                        <div className="lfx-image-lightbox-price-caption">{lightboxOfferCaption}</div>
                      ) : null}
                    </>
                  ) : null}
                  {(skuDisplay || vendorBadge) ? (
                    <div className="lfx-image-lightbox-codes-meta">
                      {skuDisplay ? (
                        <div className="lfx-image-lightbox-sku-code">
                          <span className="lfx-image-lightbox-sku-code-value">{skuDisplay}</span>
                        </div>
                      ) : null}
                      {vendorBadge ? (
                        <div className="lfx-image-lightbox-vendor-code">
                          <span className="lfx-image-lightbox-vendor-code-value">{vendorBadge}</span>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>,
            document.body
          )
        : null}
      {colorOptions.length > 0 && (
        <div className="lfx-pd-gallery-colors">
          <div className="lfx-pd-gallery-strip">
            {colorStripNav.show ? (
              <button
                type="button"
                className={`lfx-pd-strip-nav${colorStripNav.canPrev ? "" : " is-disabled"}`}
                onClick={() => scrollStrip(colorThumbsRef, -220, setColorStripNav, { minItemCount: colorOptions.length })}
                aria-label="Scroll colors left"
              >
                <ChevronLeft size={14} />
              </button>
            ) : null}
            <div
              className={`lfx-pd-gallery-colors-row${colorStripNav.show ? " has-strip-nav" : ""}`}
              ref={colorThumbsRef}
              onScroll={() => computeStripNav(colorThumbsRef, setColorStripNav, { minItemCount: colorOptions.length })}
            >
            {colorOptions.map((color) => {
              const colorMapKey = pickColorKey(colorImageMap, color);
              const variantMapKey = pickColorKey(variantGalleryMap, color);
              const mapped = colorMapKey ? colorImageMap?.[colorMapKey] : "";
              const variantFirst = variantMapKey
                ? normalizeImageArray(Object.values(variantGalleryMap[variantMapKey])[0])[0]
                : "";
              const thumbSrc = mapped || variantFirst;
              const isActive = selectedColor === color;
              return (
                <div key={`color-${color}`} className="lfx-pd-gallery-thumb-wrap lfx-pd-gallery-thumb-wrap--color">
                  <button
                    type="button"
                    onClick={() => setSelectedColor(color)}
                    title={thumbSrc ? color : `${color} image not mapped`}
                    className={`lfx-pd-gallery-thumb-btn lfx-pd-gallery-thumb-btn--color${isActive ? " lfx-pd-gallery-thumb-btn--active" : ""}${thumbSrc ? "" : " lfx-pd-gallery-thumb-btn--empty"}`}
                  >
                    {thumbSrc ? (
                      <>
                        <img
                          src={thumbSrc}
                          alt={color}
                          loading="lazy"
                          className="lfx-pd-gallery-thumb-img"
                          onLoad={() => computeStripNav(colorThumbsRef, setColorStripNav, { minItemCount: colorOptions.length })}
                        />
                        {stockBadge ? <span className={`lfx-stock-badge lfx-stock-badge--thumb ${stockBadgeClass}`}>{stockBadge}</span> : null}
                      </>
                    ) : (
                      <span className="lfx-pd-gallery-color-placeholder">{color}</span>
                    )}
                  </button>
                  <div className="lfx-pd-gallery-thumb-caption">{color}</div>
                </div>
              );
            })}
            </div>
            {colorStripNav.show ? (
              <button
                type="button"
                className={`lfx-pd-strip-nav${colorStripNav.canNext ? "" : " is-disabled"}`}
                onClick={() => scrollStrip(colorThumbsRef, 220, setColorStripNav, { minItemCount: colorOptions.length })}
                aria-label="Scroll colors right"
              >
                <ChevronRight size={14} />
              </button>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
